import React, { useEffect, useMemo, useState } from "react";
import { Link, useLocation, useParams } from "react-router-dom";
import { ArrowLeft, Plus, Search } from "lucide-react";
import { accountAuthError, resendInvitation } from "../../../api/accountAuthApi";
import {
  getClient,
  getEffectivePermissions,
  listClientRoles,
  listClientUsers,
  updateClientMembership,
} from "../../../api/chatbotApi";
import { authStore } from "../../../util/authStore";
import { isClientAdmin, isPlatformAdmin } from "../../../util/accessControl";
import useAuth from "../../../hooks/useAuth";

export default function ChatbotClientUsersPage() {
  const { clientId } = useParams();
  const location = useLocation();
  const [notice, setNotice] = useState(location.state?.invitationNotice || "");
  const [memberships, setMemberships] = useState(null);
  const [search, setSearch] = useState("");
  const [roles, setRoles] = useState([]);
  const [clientName, setClientName] = useState("");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);
  const [version, setVersion] = useState(0);
  const [editing, setEditing] = useState(null);
  const [selectedRoleIds, setSelectedRoleIds] = useState([]);
  const [effective, setEffective] = useState({});
  const { user: currentUser } = useAuth();
  const authorized = isClientAdmin(currentUser, clientId);

  useEffect(() => {
    let active = true;
    if (!authorized) return () => { active = false; };
    setClientName("");
    Promise.all([listClientUsers(clientId), listClientRoles(clientId), getClient(clientId)])
      .then(([membershipsResult, rolesResult, client]) => {
        if (!Array.isArray(membershipsResult?.data) || !Array.isArray(rolesResult?.data)) {
          throw new Error("Invalid client access response.");
        }
        if (active) {
          setMemberships(membershipsResult.data);
          setRoles(rolesResult.data);
          setClientName(client.name);
        }
      })
      .catch((err) => { if (active) setError(err.response?.data?.detail || err.message); });
    return () => { active = false; };
  }, [authorized, clientId, version]);

  const roleMap = useMemo(() => new Map(roles.map((role) => [String(role.id), role])), [roles]);
  const searchTerm = search.trim().toLowerCase();
  const filteredMemberships = (memberships || []).filter((membership) => {
    const person = membership.user || { name: membership.user_name || membership.name, email: membership.user_email || membership.email };
    const roleNames = (membership.roles || []).map((role) => role.name || roleMap.get(String(role.id))?.name || role.role_key);
    return [person.name, person.email, ...roleNames].some((value) => value?.toLowerCase().includes(searchTerm));
  });
  const update = async (membership, changes) => {
    const userId = membership.user_id ?? membership.user?.id;
    setSaving(true);
    setError("");
    try {
      await updateClientMembership(clientId, userId, {
        role_ids: membership.role_ids || (membership.roles || []).map((role) => role.id),
        is_client_admin: membership.is_client_admin === true,
        active: membership.active !== false,
        deleted: !!membership.deleted,
        ...changes,
      });
      if (String(userId) === String(authStore.getSnapshot().user?.id)) await authStore.refresh(clientId);
      setEditing(null);
      setVersion((value) => value + 1);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Unable to update client access.");
    } finally {
      setSaving(false);
    }
  };

  const openRoleEditor = (membership) => {
    setEditing(membership);
    setSelectedRoleIds((membership.role_ids || (membership.roles || []).map((role) => role.id)).map(String));
  };
  const toggleRole = (roleId) => setSelectedRoleIds((current) => (
    current.includes(String(roleId)) ? current.filter((id) => id !== String(roleId)) : [...current, String(roleId)]
  ));
  const toggleEffective = async (userId) => {
    if (effective[userId]) {
      setEffective((current) => ({ ...current, [userId]: null }));
      return;
    }
    try {
      const result = await getEffectivePermissions(clientId, userId);
      setEffective((current) => ({ ...current, [userId]: result.data }));
    } catch (err) {
      setError(err.response?.data?.detail || err.message || "Unable to load effective permissions.");
    }
  };

  if (!authorized) return <section className="rounded-xl bg-white p-8"><h1 className="text-xl font-bold">Access denied</h1><p className="mt-2 text-slate-600">Only Super Admins and Client Admins can manage client users and roles.</p></section>;

  return <section className="space-y-5">
    <nav aria-label="Breadcrumb" className="flex items-center gap-2 text-sm text-slate-500">
      <Link to="/clients" className="inline-flex items-center gap-1 transition hover:text-slate-900">
        <ArrowLeft size={16} /> Clients
      </Link>
      <span>/</span>
      <Link to={`/clients/${clientId}`} className="transition hover:text-slate-900">
        {clientName || "Client"}
      </Link>
      <span>/</span>
      <span aria-current="page" className="font-semibold text-slate-900">Users</span>
    </nav>
    <div className="flex flex-wrap items-center justify-between gap-3">
      <div><h1 className="text-2xl font-bold">{clientName ? `${clientName}'s Team` : "Team"}</h1><p className="mt-1 text-sm text-slate-600">Membership roles combine additively. Permissions apply to all conversations and records in this client.</p></div>
      <nav aria-label="Client access navigation" className="flex flex-wrap gap-2">
        <Link to={`/clients/${clientId}/roles`} className="rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-semibold text-slate-700">Roles</Link>
        <Link to={`/clients/${clientId}/users`} aria-current="page" className="rounded-lg bg-cyan-800 px-4 py-2 text-sm font-semibold text-white">Users</Link>
      </nav>
    </div>
    {error && <p role="alert" className="rounded-lg bg-rose-50 p-3 text-rose-700">{error}</p>}
    {notice && <p role="status" className="rounded-lg bg-cyan-50 p-3 text-cyan-900">{notice}</p>}
    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
      <h2 className="text-xl font-bold">Users{memberships !== null ? ` (${memberships.length})` : ""}</h2>
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:gap-4">
        <div className="relative w-full sm:w-80">
          <Search size={18} aria-hidden="true" className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <input type="search" aria-label="Search users" placeholder="Search users by name, email, or role..." value={search} onChange={(event) => setSearch(event.target.value)} className="w-full rounded-lg border border-slate-300 bg-white py-2.5 pl-10 pr-3 text-sm" />
        </div>
        <Link to={`/clients/${clientId}/users/new-user`} className="inline-flex items-center justify-center gap-2 whitespace-nowrap rounded-lg bg-indigo-700 px-4 py-2.5 font-semibold text-white"><Plus size={18} aria-hidden="true" />Add user</Link>
      </div>
    </div>
    {memberships === null ? <p role="status">Loading memberships…</p> : memberships.length === 0 ? <p role="status" className="rounded-xl border bg-white p-10 text-center text-slate-500">No users yet.</p> : filteredMemberships.length === 0 ? <p role="status" className="rounded-xl border bg-white p-10 text-center text-slate-500">No users match your search.</p> : <div className="overflow-x-auto rounded-xl border bg-white"><table className="w-full text-left">
      <thead className="bg-slate-50"><tr>{["Name", "Email", "Access", "Roles", "Status", "Actions"].map((label) => <th key={label} className="p-3">{label}</th>)}</tr></thead>
      <tbody>{filteredMemberships.map((membership) => {
        const person = membership.user || { name: membership.user_name || membership.name, email: membership.user_email || membership.email };
        const id = membership.user_id ?? membership.user?.id;
        const assigned = membership.roles || [];
        return <React.Fragment key={id}><tr className="border-t">
          <td className="p-3">{person.name || person.email || `User ${id}`}</td>
          <td className="p-3">{person.email}</td>
          <td className="p-3">{membership.is_client_admin ? <span className="font-semibold text-indigo-700">Client Admin</span> : "Custom access"}</td>
          <td className="p-3">{assigned.length ? assigned.map((role) => role.name || roleMap.get(String(role.id))?.name || role.role_key).join(", ") : "No custom roles"}</td>
          <td className="p-3">{membership.deleted ? "Removed" : membership.active === false ? "Revoked" : membership.must_change_password ? "Pending password setup" : "Active"}
            {membership.invitation_status && <p className="mt-1 text-xs text-slate-600">Invitation: {membership.invitation_status === "accepted" ? "Accepted by email provider" : membership.invitation_status}</p>}
          </td>
          <td className="p-3"><div className="flex flex-wrap gap-2">
            <Link to={`/clients/${clientId}/users/${id}/edit`} className="text-sm font-semibold text-indigo-700">Edit user</Link>
            <button type="button" className="text-sm font-semibold text-indigo-700" onClick={() => openRoleEditor(membership)}>Edit access</button>
            <button type="button" className="text-sm font-semibold text-slate-700" onClick={() => toggleEffective(id)}>Effective permissions</button>
            {membership.must_change_password && !membership.deleted && membership.active !== false && <button type="button" disabled={saving}
              className="text-sm font-semibold text-cyan-800" onClick={async () => {
                setSaving(true); setError(""); setNotice("");
                try {
                  await resendInvitation(id);
                  setNotice("A new invitation is queued. The previous setup link is no longer valid.");
                  setVersion((value) => value + 1);
                } catch (err) { setError(accountAuthError(err)); }
                finally { setSaving(false); }
              }}>Resend invitation</button>}
            {membership.deleted ? <button type="button" disabled={saving} onClick={() => update(membership, { active: true, deleted: false })}>Restore</button>
              : <><button type="button" disabled={saving} onClick={() => update(membership, { active: membership.active === false })}>{membership.active === false ? "Grant access" : "Revoke"}</button>
                <button type="button" disabled={saving} onClick={() => update(membership, { active: false, deleted: true })}>Remove</button></>}
          </div></td>
        </tr>
        {effective[id] && <tr className="border-t bg-slate-50"><td colSpan="6" className="p-4">
          <p className="font-semibold">Effective permissions for {person.name || person.email}</p>
          <p className="mt-1 text-sm text-slate-600">Roles: {(effective[id].roles || []).map((role) => role.name).join(", ") || "None"}</p>
          <div className="mt-2 flex flex-wrap gap-2">{(effective[id].permissions || []).map((permission) => <code key={permission} className="rounded bg-white px-2 py-1 text-xs">{permission}</code>)}</div>
        </td></tr>}
      </React.Fragment>;
      })}</tbody>
    </table></div>}

    {editing && <div className="fixed inset-0 z-[100] flex items-center justify-center bg-slate-950/50 p-4" role="dialog" aria-modal="true" aria-label="Edit user access">
      <div className="max-h-[85vh] w-full max-w-xl overflow-auto rounded-2xl bg-white p-6 shadow-xl">
        <h2 className="text-xl font-bold">Edit access for {editing.user?.name || editing.user_name || editing.user?.email || editing.user_email || editing.user_id}</h2>
        <p className="mt-1 text-sm text-slate-600">Select any number of roles. Permissions are the union of these roles.</p>
        <fieldset className="mt-5 space-y-3"><legend className="mb-2 text-sm font-semibold">Custom roles</legend>
          {roles.filter((role) => role.active !== false && !role.deleted).map((role) => <label key={role.id} className="flex items-center gap-3 rounded-lg border p-3">
            <input type="checkbox" checked={selectedRoleIds.includes(String(role.id))} onChange={() => toggleRole(role.id)} />
            <span><strong>{role.name}</strong><span className="ml-2 text-xs text-slate-500">{role.role_key}</span></span>
          </label>)}
          {!roles.some((role) => role.active !== false && !role.deleted) && <p className="text-sm text-slate-500">No active custom roles. Create one in Roles &amp; Permissions.</p>}
        </fieldset>
        {isPlatformAdmin(currentUser) && <label className="mt-5 flex items-center gap-3 rounded-lg border border-amber-200 bg-amber-50 p-3">
          <input type="checkbox" checked={editing.is_client_admin === true} onChange={(event) => setEditing({ ...editing, is_client_admin: event.target.checked })} />
          <span><strong>Client Admin</strong><span className="block text-xs text-slate-600">Protected admin access; not a custom role.</span></span>
        </label>}
        <div className="mt-6 flex justify-end gap-3"><button type="button" onClick={() => setEditing(null)} className="rounded-lg border px-4 py-2">Cancel</button>
          <button type="button" disabled={saving} onClick={() => update(editing, {
            role_ids: selectedRoleIds.map((id) => /^\d+$/.test(id) ? Number(id) : id),
            is_client_admin: editing.is_client_admin === true,
          })} className="rounded-lg bg-indigo-700 px-4 py-2 font-semibold text-white disabled:opacity-50">{saving ? "Saving…" : "Save access"}</button></div>
      </div>
    </div>}
  </section>;
}
